import React, { useState, useMemo } from 'react';
import './VillageDataPanel.css';
import { FaSearch, FaSort, FaSortUp, FaSortDown, FaMapMarkerAlt, FaChartBar } from 'react-icons/fa';

const VillageDataPanel = ({ villages, onVillageSelect, onVillageHover, categoryColors, userState, userDistrict }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'waterLevel', direction: 'desc' });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'blocks'

  // Check if user is state or district admin (should hide village counts)
  const isStateOrDistrictAdmin = userState || userDistrict;

  // Sort and filter villages
  const processedVillages = useMemo(() => {
    let filtered = villages;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(v =>
        v.village.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.block.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [villages, searchTerm, sortConfig]);

  // Block-level statistics
  const blockStats = useMemo(() => {
    const blocks = {};
    
    villages.forEach(v => {
      if (!blocks[v.block]) {
        blocks[v.block] = {
          name: v.block,
          villages: [],
          avgWaterLevel: 0,
          minWaterLevel: Infinity,
          maxWaterLevel: -Infinity,
          categories: {}
        };
      }
      
      blocks[v.block].villages.push(v);
      blocks[v.block].minWaterLevel = Math.min(blocks[v.block].minWaterLevel, v.waterLevel);
      blocks[v.block].maxWaterLevel = Math.max(blocks[v.block].maxWaterLevel, v.waterLevel);
      
      blocks[v.block].categories[v.category] = (blocks[v.block].categories[v.category] || 0) + 1;
    });

    // Calculate averages
    Object.values(blocks).forEach(block => {
      block.avgWaterLevel = block.villages.reduce((sum, v) => sum + v.waterLevel, 0) / block.villages.length;
      block.criticalCount = block.villages.filter(v => v.waterLevel > 10).length;
      block.safeCount = block.villages.filter(v => v.waterLevel <= 5).length;
    });

    return Object.values(blocks).sort((a, b) => b.avgWaterLevel - a.avgWaterLevel);
  }, [villages]);

  // Overall statistics
  const overallStats = useMemo(() => {
    if (villages.length === 0) return null;

    const waterLevels = villages.map(v => v.waterLevel);
    const avg = waterLevels.reduce((sum, wl) => sum + wl, 0) / waterLevels.length;
    const min = Math.min(...waterLevels);
    const max = Math.max(...waterLevels);

    const categoryCounts = {};
    villages.forEach(v => {
      categoryCounts[v.category] = (categoryCounts[v.category] || 0) + 1;
    });

    return {
      total: villages.length,
      avg: avg.toFixed(2),
      min: min.toFixed(2),
      max: max.toFixed(2),
      critical: villages.filter(v => v.waterLevel > 10).length,
      safe: villages.filter(v => v.waterLevel <= 5).length,
      categoryCounts
    };
  }, [villages]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const getWaterLevelColor = (waterLevel) => {
    if (waterLevel <= 2) return '#1fc21f';
    if (waterLevel <= 5) return '#7ed321';
    if (waterLevel <= 10) return '#f9c802';
    if (waterLevel <= 15) return '#ff8d1a';
    return '#ea2328';
  };

  const getWaterLevelStatus = (waterLevel) => {
    if (waterLevel <= 2) return 'Excellent';
    if (waterLevel <= 5) return 'Good';
    if (waterLevel <= 10) return 'Moderate';
    if (waterLevel <= 15) return 'Critical';
    return 'Severe';
  };

  // Generate insights/conclusion
  const generateInsights = () => {
    if (!overallStats) return null;

    const criticalPercentage = ((overallStats.critical / overallStats.total) * 100).toFixed(1);
    const safePercentage = ((overallStats.safe / overallStats.total) * 100).toFixed(1);
    
    let status, statusColor, recommendation;
    
    if (overallStats.critical > overallStats.safe) {
      status = 'Critical Situation';
      statusColor = '#ea2328';
      recommendation = 'Immediate intervention required. Focus on villages with water levels >10m. Implement rainwater harvesting and restrict groundwater extraction.';
    } else if (overallStats.critical > overallStats.total * 0.3) {
      status = 'Moderate Concern';
      statusColor = '#ff8d1a';
      recommendation = 'Significant number of villages under stress. Monitor closely and implement conservation measures in critical villages.';
    } else if (overallStats.critical > 0) {
      status = 'Generally Stable';
      statusColor = '#f9c802';
      recommendation = 'Most villages are safe, but attention needed for critical villages. Continue monitoring and preventive measures.';
    } else {
      status = 'Healthy Status';
      statusColor = '#1fc21f';
      recommendation = 'All villages have good water availability. Maintain current practices and continue regular monitoring.';
    }

    return { status, statusColor, recommendation, criticalPercentage, safePercentage };
  };

  const insights = generateInsights();

  if (!villages || villages.length === 0) {
    return (
      <div className="village-data-panel">
        <div className="village-empty-state">
          <FaMapMarkerAlt size={48} color="#ccc" />
          <p>No village data available for this region</p>
          <small>Select a state or district with village-level monitoring</small>
        </div>
      </div>
    );
  }

  return (
    <div className="village-data-panel">
      {/* Header with Stats */}
      <div className="village-panel-header">
        <h3>
          <FaMapMarkerAlt /> Village-Level Groundwater Data
        </h3>
        <div className="village-view-toggle">
          <button
            className={viewMode === 'table' ? 'active' : ''}
            onClick={() => setViewMode('table')}
          >
            Table View
          </button>
          <button
            className={viewMode === 'blocks' ? 'active' : ''}
            onClick={() => setViewMode('blocks')}
          >
            Block Summary
          </button>
        </div>
      </div>

      {/* Overall Statistics */}
      {overallStats && (
        <div className="village-overall-stats">
          {!isStateOrDistrictAdmin && (
            <div className="village-stat-card">
              <div className="village-stat-value">{overallStats.total}</div>
              <div className="village-stat-label">Total Villages</div>
            </div>
          )}
          <div className="village-stat-card">
            <div className="village-stat-value">
              {overallStats.avg} m
            </div>
            <div className="village-stat-label">Avg Water Level</div>
          </div>
          {!isStateOrDistrictAdmin && (
            <div className="village-stat-card">
              <div className="village-stat-value">
                {overallStats.safe}
              </div>
              <div className="village-stat-label">Safe Villages</div>
            </div>
          )}
          {!isStateOrDistrictAdmin && (
            <div className="village-stat-card">
              <div className="village-stat-value">
                {overallStats.critical}
              </div>
              <div className="village-stat-label">Critical Villages</div>
            </div>
          )}
        </div>
      )}

      {/* Search Bar */}
      <div className="village-search-bar">
        <FaSearch className="village-search-icon" />
        <input
          type="text"
          placeholder="Search villages or blocks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="village-search-input"
        />
        {searchTerm && (
          <button className="village-search-clear" onClick={() => setSearchTerm('')}>
            ×
          </button>
        )}
      </div>

      {/* Content based on view mode */}
      {viewMode === 'table' ? (
        <div className="village-table-container">
          <table className="village-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('village')}>
                  Village {getSortIcon('village')}
                </th>
                <th onClick={() => handleSort('block')}>
                  Block {getSortIcon('block')}
                </th>
                <th onClick={() => handleSort('waterLevel')}>
                  Water Level (mbgl) {getSortIcon('waterLevel')}
                </th>
                <th>Status</th>
                <th onClick={() => handleSort('category')}>
                  Category {getSortIcon('category')}
                </th>
              </tr>
            </thead>
            <tbody>
              {processedVillages.map((village, index) => (
                <tr
                  key={`${village.village}-${index}`}
                  className="village-table-row"
                  onClick={() => onVillageSelect && onVillageSelect(village)}
                  onMouseEnter={() => onVillageHover && onVillageHover(village)}
                  onMouseLeave={() => onVillageHover && onVillageHover(null)}
                >
                  <td className="village-name-cell">
                    <strong>{village.village}</strong>
                  </td>
                  <td>{village.block}</td>
                  <td>
                    <div className="village-water-level-cell">
                      <span
                        className="village-water-level-value"
                        style={{ color: getWaterLevelColor(village.waterLevel) }}
                      >
                        {village.waterLevel.toFixed(1)}
                      </span>
                      <div
                        className="village-water-level-bar"
                        style={{
                          width: `${Math.min((village.waterLevel / 20) * 100, 100)}%`,
                          backgroundColor: getWaterLevelColor(village.waterLevel)
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <span
                      className="village-status-badge"
                      style={{
                        backgroundColor: getWaterLevelColor(village.waterLevel),
                        color: '#fff'
                      }}
                    >
                      {getWaterLevelStatus(village.waterLevel)}
                    </span>
                  </td>
                  <td>
                    <span
                      className="village-category-badge"
                      style={{
                        backgroundColor: categoryColors[village.category] || '#e6e6e6',
                        color: '#fff'
                      }}
                    >
                      {village.category}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="village-blocks-container">
          {blockStats.map((block, index) => (
            <div key={index} className="village-block-card">
              <div className="village-block-header">
                <h4>{block.name}</h4>
                {!isStateOrDistrictAdmin && (
                  <span className="village-block-count">{block.villages.length} villages</span>
                )}
              </div>
              
              <div className="village-block-stats">
                <div className="village-block-stat">
                  <span className="village-block-stat-label">Avg Depth:</span>
                  <span
                    className="village-block-stat-value"
                    style={{ color: getWaterLevelColor(block.avgWaterLevel) }}
                  >
                    {block.avgWaterLevel.toFixed(1)} m
                  </span>
                </div>
                <div className="village-block-stat">
                  <span className="village-block-stat-label">Range:</span>
                  <span className="village-block-stat-value">
                    {block.minWaterLevel.toFixed(1)} - {block.maxWaterLevel.toFixed(1)} m
                  </span>
                </div>
              </div>

              <div className="village-block-distribution">
                <div className="village-block-dist-label">Distribution:</div>
                <div className="village-block-dist-bars">
                  <div className="village-block-dist-item">
                    {!isStateOrDistrictAdmin && (
                      <span className="village-block-dist-count" style={{ color: '#1fc21f' }}>
                        {block.safeCount}
                      </span>
                    )}
                    <span className="village-block-dist-text">Safe</span>
                  </div>
                  <div className="village-block-dist-item">
                    {!isStateOrDistrictAdmin && (
                      <span className="village-block-dist-count" style={{ color: '#ea2328' }}>
                        {block.criticalCount}
                      </span>
                    )}
                    <span className="village-block-dist-text">Critical</span>
                  </div>
                </div>
              </div>

              <div className="village-block-progress">
                <div
                  className="village-block-progress-bar"
                  style={{
                    width: `${(block.criticalCount / block.villages.length) * 100}%`,
                    backgroundColor: block.criticalCount > block.safeCount ? '#ea2328' : '#f9c802'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Insights & Conclusion Section */}
      {insights && (
        <div className="village-insights-section">
          <h4 className="insights-title">📊 Key Insights</h4>
          
          <div className="insights-status-card" style={{ borderLeftColor: insights.statusColor }}>
            <div className="insights-status-header">
              <span className="insights-status-icon" style={{ color: insights.statusColor }}>●</span>
              <span className="insights-status-text" style={{ color: insights.statusColor }}>
                {insights.status}
              </span>
            </div>
            <p className="insights-recommendation">{insights.recommendation}</p>
          </div>

          <div className="insights-stats-grid">
            {!isStateOrDistrictAdmin && (
              <div className="insights-stat-box">
                <div className="insights-stat-label">Critical Villages</div>
                <div className="insights-stat-value" style={{ color: '#ea2328' }}>
                  {overallStats.critical} ({insights.criticalPercentage}%)
                </div>
                <div className="insights-stat-desc">Need immediate attention</div>
              </div>
            )}

            {!isStateOrDistrictAdmin && (
              <div className="insights-stat-box">
                <div className="insights-stat-label">Safe Villages</div>
                <div className="insights-stat-value" style={{ color: '#1fc21f' }}>
                  {overallStats.safe} ({insights.safePercentage}%)
                </div>
                <div className="insights-stat-desc">Good water availability</div>
              </div>
            )}

            <div className="insights-stat-box">
              <div className="insights-stat-label">Average Depth</div>
              <div className="insights-stat-value" style={{ color: getWaterLevelColor(parseFloat(overallStats.avg)) }}>
                {overallStats.avg}m
              </div>
              <div className="insights-stat-desc">District-wide average</div>
            </div>

            <div className="insights-stat-box">
              <div className="insights-stat-label">Depth Range</div>
              <div className="insights-stat-value" style={{ color: '#666' }}>
                {overallStats.min}m - {overallStats.max}m
              </div>
              <div className="insights-stat-desc">Min to max water level</div>
            </div>
          </div>

          <div className="insights-actions">
            <h5>Recommended Actions:</h5>
            <ul>
              {overallStats.critical > 0 && !isStateOrDistrictAdmin && (
                <li>🎯 Prioritize {overallStats.critical} critical villages for immediate intervention</li>
              )}
              {overallStats.critical > 0 && isStateOrDistrictAdmin && (
                <li>🎯 Prioritize critical areas for immediate intervention</li>
              )}
              {parseFloat(overallStats.avg) > 10 && (
                <li>💧 Implement district-wide water conservation programs</li>
              )}
              {overallStats.critical > overallStats.safe && (
                <li>🚨 Declare water stress alert and restrict non-essential groundwater use</li>
              )}
              {!isStateOrDistrictAdmin && (
                <li>📊 Continue monthly monitoring of all {overallStats.total} villages</li>
              )}
              {isStateOrDistrictAdmin && (
                <li>📊 Continue monthly monitoring of all areas</li>
              )}
              <li>🌧️ Promote rainwater harvesting in areas with water levels &gt;5m</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default VillageDataPanel;